import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReportTemplate from '../../../components/reportPage/ReportTemplate';
import { fromInternalVehicle } from '../../../components/reportPage/adapters/internal';
import { useReportService } from '../../../services/reportService';
import { isReportUnavailable } from '../../../components/reportPage/reportAccess';
import { useOwnReport } from '../../../components/reportPage/useOwnReport';
import sampleData from '../sampleCombinedReportData.json';
import {
    translationNotice,
    translationStatus,
    useRetry,
    withTranslationNotice,
} from '../../../components/reportPage/translationNotice';

/** Internal report: our own inspection of a car in our database (a1_cars), no dealer documents. */
const InternalReport = ({ sample, shared }) => {
    const { t, i18n } = useTranslation('reportPage');
    const { vin, sharedLink } = useParams();
    const { getInternalReport, getInternalReportBySharedLink } = useReportService();
    const [state, setState] = useState({ vehicle: null, loading: true, denied: false });
    const [attempt, retry] = useRetry();

    const fetchVehicle = () =>
        shared
            ? getInternalReportBySharedLink(sharedLink, i18n.resolvedLanguage)
            : getInternalReport(vin, i18n.resolvedLanguage);

    useEffect(() => {
        let cancelled = false;
        if (sample) {
            setState({ vehicle: sampleData, loading: false });
            return undefined;
        }
        if (!vin && !shared) {
            setState({ vehicle: null, loading: false });
            return undefined;
        }

        setState(current => ({ ...current, loading: true }));
        fetchVehicle()
            .then(vehicle => !cancelled && setState({ vehicle, loading: false, denied: false }))
            .catch(
                error => !cancelled && setState({ vehicle: null, loading: false, denied: isReportUnavailable(error) })
            );

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vin, sharedLink, sample, shared, i18n.resolvedLanguage]);

    // Try again on the translation notice asks quietly: the page and the reader's place stay until the answer comes
    useEffect(() => {
        if (!attempt) return undefined;
        let cancelled = false;
        fetchVehicle()
            .then(vehicle => !cancelled && vehicle && setState(current => ({ ...current, vehicle })))
            .catch(() => undefined);
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attempt]);

    const denied = !sample && !shared && state.denied;
    const own = useOwnReport(vin, denied);
    const report = useMemo(
        () =>
            withTranslationNotice(
                fromInternalVehicle(state.vehicle, t, { standalone: true }),
                translationNotice(translationStatus(state.vehicle && state.vehicle.translation), t, retry)
            ),
        [state.vehicle, t, retry]
    );

    return (
        <ReportTemplate
            report={report}
            loading={state.loading || own.loading}
            unavailable={denied ? { ownRecord: own.record } : null}
            showBack={!shared && !sample}
            printTitle={state.vehicle ? `${String(state.vehicle.vin || '').toUpperCase()} - CheckVin.ai` : undefined}
        />
    );
};

export default InternalReport;
